import React, { useCallback, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import _ from "lodash";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { OnOffSwitch, PlainTooltip, CircleSwitcherItem, Switcher } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import type { SelectorHandlers } from "app/components/filters-bar/useSelectorTrigger";
import { MMXAlertWithPlainTooltip } from "app/components/MMXAlertWithPlainTooltip";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter } from "app/filters/i18nFilter";
import { useHDBannerCta } from "app/marketing/components/common/banners/HDBanner/useHDBannerCta";
import { SwitcherGranularityContainer } from "app/pages/website-analysis/components/SwitcherGranularityContainer";
import { ChannelAnalysisChart } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/ChannelAnalysisChart";
import { filtersList } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/ChannelAnalysisChartConfig";
import {
    useGranularities,
    useMTD,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/ChannelAnalysisChartContainerHooks";
import {
    StyledSubtitle,
    SubTitleChannelAnalysisChart,
} from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/StyledComponents";
import { Loader } from "app/pages/website-analysis/traffic-sources/mmx/components/common/Loader";
import { calcInsights } from "app/pages/website-analysis/traffic-sources/mmx/components/InsightsAssistant/insights-calculator";
import type { IInsightProps } from "app/pages/website-analysis/traffic-sources/mmx/components/InsightsAssistant/InsightsContainer";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { allTrackers } from "app/services/track/track";
import { trafficChannelsService } from "app/services/TrafficChannelsService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { monthToDateTracking } from "app/UtilitiesAndConstants/UtilityFunctions/monthsToDateUtilityFunctions";
import { NoDataTitle } from "pro-features/components/NoData/src/NoData";
import {
    BoxContainer,
    ContentContainer,
    TitleContainer,
} from "pro-features/pages/conversion/components/benchmarkOvertime/StyledComponents";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { PrimaryBoxTitle } from "pro-features/styled components/StyledBoxTitle/src/StyledBoxTitle";
import { FlexColumn, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useChannelData } from "../common/helpers";
import {
    isDurationAfterSplit,
    minDate,
    removeSearchChannel,
    removeSearchChannelTotal,
    removeSplitSearchChannels,
    removeSplitSearchChannelsTotal,
} from "../common/MobileSplit-utils";
import { parseData } from "../InsightsAssistant/data-parser";

const DATA_PATH_BY_GRANULARITY: Record<string, string> = {
    Monthly: "MonthlyData",
    Weekly: "WeeklyData",
    Daily: "DailyData",
};

export interface ISiteLegendItem {
    name: string;
    color: string;
    icon: string;
    image: string;
    smallIcon: boolean;
    isSuggested?: boolean;
}

export type ISiteLegendItems = Array<ISiteLegendItem>;

interface IChosenSites {
    sitelistForLegend(): ISiteLegendItem[];

    count: () => number;
    listInfo: {};
}

interface ISubTitleProps extends SelectorHandlers {
    fromDate: dayjs.Dayjs;
    toDate: dayjs.Dayjs;
    country: number;
    webSource: string;
    duration: string;
    compareFromDate?: dayjs.Dayjs | null;
    compareToDate?: dayjs.Dayjs | null;
}

const SubTitle = ({
    fromDate,
    toDate,
    country,
    webSource,
    duration,
    compareFromDate = null,
    compareToDate = null,
    onDateClick,
    onCountryClick,
    onDateHover,
    onCountryHover,
    onWebSourceHover,
}: ISubTitleProps) => {
    const subtitleFilters = useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: fromDate.valueOf(),
                    to: toDate.valueOf(),
                    useRangeDisplay: duration !== "28d" && fromDate.format("YYYY-MM") !== toDate.format("YYYY-MM"),
                    compareFrom: compareFromDate && compareFromDate.valueOf(),
                    compareTo: compareToDate && compareToDate.valueOf(),
                },
                onClick: onDateClick,
                onMouseEnter: onDateHover,
            },
            {
                filter: "country",
                countryCode: country,
                value: CountryService.getCountryById(country)?.text,
                onClick: onCountryClick,
                onMouseEnter: onCountryHover,
            },
            {
                filter: "webSource",
                value: webSource,
                onMouseEnter: onWebSourceHover,
            },
        ],
        [
            fromDate,
            toDate,
            country,
            webSource,
            duration,
            onDateClick,
            onCountryClick,
            onDateHover,
            onCountryHover,
            onWebSourceHover,
        ],
    );
    return <StyledSubtitle filters={subtitleFilters} />;
};

const Wrapper = styled.div`
    margin-bottom: 22px;

    .gran-switch span:last-child button {
        border-right: 1px solid ${colorsPalettes.carbon["50"]};
    }

    scroll-margin-top: 40px;
`;

const Content = styled(ContentContainer)`
    padding-top: 0;
    justify-content: center;
    align-items: center;
    min-height: 283px;

    ${NoDataTitle} {
        max-width: none;
    }
`;

const Title = styled(TitleContainer)`
    padding: 16px 16px 24px 24px;
`;

const RightSection = styled(FlexRow)`
    align-items: center;
`;

const MTDLabel = styled.span`
    margin: 0 16px 0 8px;
`;

export const MTDTitle = styled(FlexRow)`
    cursor: pointer;
`;

const StyledSwitcher = styled(Switcher)`
    height: 40px;
`;

const MMXAlertWrapper = styled.div`
    margin-top: 3px;
    align-self: center;
    margin-left: 2px;
`;

const HeaderActions = styled(FlexRow)`
    align-items: center;
    gap: 4px;
    margin-left: 12px;
`;

export const mapTabToMetricAndType = {
    TrafficShare: {
        metric: "ChannelsAnalysisByTrafficShare",
        type: "MmxTrafficShareDashboard",
    },
    AverageDuration: {
        metric: "ChannelsAnalysisByAverageDuration",
        type: "MmxVisitDurationDashboard",
    },
    PagesPerVisit: {
        metric: "ChannelsAnalysisByPagesPerVisit",
        type: "MmxPagesPerVisitDashboard",
    },
    BounceRate: {
        metric: "ChannelsAnalysisByBounceRate",
        type: "MmxBounceRateDashboard",
    },
};

const ChannelAnalysisChartContainer: React.FunctionComponent<SelectorHandlers> = ({
    onDateClick,
    onCountryClick,
    onDateHover,
    onCountryHover,
    onWebSourceHover,
}) => {
    const { params } = useCurrentStateAndParams();
    const {
        key,
        channelAnalysisMtd,
        channelAnalysisMetric = "TrafficShare",
        channelAnalysisGranularity,
        channelAnalysisChannel = "Direct",
        country,
        duration,
        comparedDuration,
        isWWW,
    } = params;

    const hdBannerCtaState = useHDBannerCta({ trackingName: "Graph tooltip/channel traffic" });
    /*
     * The add-on availability arrives after the chart config is built, and the tooltip formatter lives
     * on in that config, so the banner state is read when the tooltip opens rather than closed over
     */
    const hdBannerCtaStateRef = useRef(hdBannerCtaState);
    hdBannerCtaStateRef.current = hdBannerCtaState;
    const getHdBannerCtaState = useCallback(() => hdBannerCtaStateRef.current, []);

    const webSource = params.webSource;
    const activeChannels = params?.activeChannels?.split(",");
    const component = swSettings.current;
    const newMMXAlgoStartData = component.resources.NewAlgoMMX;
    const durationObject = useMemo(
        () => DurationService.getDurationData(duration, comparedDuration),
        [duration, comparedDuration, component],
    );
    const periodDuration = useMemo(
        () => durationObject.raw.to.diff(durationObject.raw.from, "months"),
        [durationObject],
    );
    const updateUrlParam = (granularity) => {
        // without settimeout $digest already in progress error occurs
        setTimeout(() => proNavigator.updateParams({ channelAnalysisGranularity: granularity.value }), 0);
    };
    const { selectedGranularity, availableGranularities, updateGranularity } = useGranularities({
        isWindow,
        isPeriodOverPeriod,
        periodDuration,
        urlGranularityParam: channelAnalysisGranularity,
        updateUrlParam,
    });
    const keys = useMemo(() => chosenSites.sitelistForLegend(), [key]);
    const graphComponentRef = useRef<ChannelAnalysisChart>(null);
    const wrapperRef = React.useRef<HTMLInputElement>(null);
    const [selectedVisualizationIndex, setSelectedVisualizationIndex] = useState<number>(1);
    const showInsights = useMemo(
        () =>
            selectedGranularity.value !== "Daily" &&
            periodDuration < 3 &&
            webSource === "Desktop" &&
            !isPeriodOverPeriod(),
        [selectedGranularity],
    );

    function isWindow() {
        return durationObject.forAPI.isWindow;
    }

    function isPeriodOverPeriod() {
        return !!proNavigator.getParams().comparedDuration;
    }

    function isComparePop() {
        return isPeriodOverPeriod() && chosenSites.count() >= 1;
    }

    const { isMTDActive, isMTDSupported, setMTD } = useMTD({
        isWindow,
        disableMTD: false,
        isPeriodOverPeriod,
        durationObject,
        component,
        urlMtdParam: channelAnalysisMtd ? JSON.parse(channelAnalysisMtd) : null, // Converting "false" to boolean. since Boolean("false") > true ¯\_(ツ)_/¯
    });

    function toggleGranularity(index) {
        const nextGranularity = availableGranularities.find(({ index: idx }) => idx === index).value;
        proNavigator.updateParams({ channelAnalysisGranularity: nextGranularity });
        updateGranularity(nextGranularity);
        allTrackers.dispatchTrackEvent(
            "Time Frame Button",
            "switch",
            `Over Time Graph/${channelAnalysisMetric || ""}/${nextGranularity || ""}`,
        );
    }

    function toggleMTD() {
        const nextMTD = !isMTDActive;
        proNavigator.updateParams({ channelAnalysisMtd: nextMTD });
        setMTD();
        monthToDateTracking()(nextMTD);
    }

    const handleChannelFilterChange = (value) => {
        proNavigator.updateParams({ channelAnalysisChannel: value });
    };
    const handleMetricChange = (index) => {
        proNavigator.updateParams({ channelAnalysisMetric: index });
    };

    function getLastWindowDate() {
        return swSettings.current.lastSupportedDailyDate;
    }

    const getDateRange = (durationObject, isMTDActive) => {
        if (!isMTDActive) {
            return [durationObject.raw.from, durationObject.raw.to];
        }
        return [durationObject.raw.from, getLastWindowDate()];
    };

    const getCompareDateRange = (durationObject) => {
        return [durationObject.raw.compareFrom, durationObject.raw.compareTo];
    };
    const {
        data,
        isLoading: isFetching,
        getExcelUrl,
    } = useChannelData({
        params: { country, duration, isWWW, key, webSource },
        durationObject,
        isMTDActive,
        granularity: selectedGranularity,
        isPeriodOverPeriod: isPeriodOverPeriod(),
        withExcel: true,
    });

    const [startDate, endDate] = getDateRange(durationObject, false); // regular from,to (not includes MTD)
    const [fromDate, toDate] = getDateRange(durationObject, isMTDActive); // real date range which can be affected by MTD
    const [compareFromDate, compareToDate] = getCompareDateRange(durationObject);

    const isCompare = keys.length > 1;
    const isPoP = isPeriodOverPeriod();
    const downloadExcelPermitted = component.resources.IsExcelAllowed;
    const isPaidExcelPermitted = isPaidExcelAllowed();
    const trafficChannelsCount =
        webSource === devicesTypes.TOTAL
            ? filtersList.Total.length
            : trafficChannelsService.getAvailableTrafficChannels().length;

    const filterChannels = useMemo(() => {
        const base: string[] = filtersList[webSource] ?? [];
        if (webSource !== devicesTypes.MOBILE && webSource !== devicesTypes.TOTAL) {
            return base;
        }
        const splitDate = isPoP
            ? minDate(durationObject.forAPI.from, durationObject.forAPI.compareFrom)
            : startDate.format("YYYY-MM-DD");
        const isAfterSplit = isDurationAfterSplit(splitDate);
        const isTotal = webSource === devicesTypes.TOTAL;
        if (isAfterSplit) {
            return isTotal && !isCompare ? removeSearchChannelTotal(base) : removeSearchChannel(base);
        }
        return isTotal && !isCompare ? removeSplitSearchChannelsTotal(base) : removeSplitSearchChannels(base);
    }, [webSource, isCompare, isPoP, durationObject, startDate]);

    const safeSelectedChannel = filterChannels.includes(channelAnalysisChannel)
        ? channelAnalysisChannel
        : (filterChannels[0] ?? "");

    const excelTotalCount = useMemo(() => {
        const granKey = DATA_PATH_BY_GRANULARITY[selectedGranularity.value];
        const dataRecord = data as Record<string, any> | undefined;
        const path =
            webSource === devicesTypes.DESKTOP
                ? dataRecord?.[granKey]?.[channelAnalysisMetric]
                : dataRecord?.Data?.[channelAnalysisMetric];
        const finale = path && (isCompare ? path[safeSelectedChannel] : path.Data);
        const breakdownCount = Object.keys(finale?.BreakDown ?? {}).length;
        return Math.max(keys.length, breakdownCount) * trafficChannelsCount;
    }, [
        data,
        selectedGranularity.value,
        channelAnalysisMetric,
        isCompare,
        safeSelectedChannel,
        webSource,
        keys.length,
        trafficChannelsCount,
    ]);

    const handleExcelClick = () => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", `${channelAnalysisMetric || ""}/Excel`);
    };

    const handlePngDownload = (): Promise<void> => {
        return graphComponentRef.current?.downloadPng?.() ?? Promise.resolve();
    };

    const insights = useMemo(() => {
        if (data && !isFetching) {
            return getInsights();
        }
    }, [data, isFetching]);

    function getMTDTooltipContent() {
        return i18nFilter()("wa.mmx.channel_analysis.mtd.tooltip.last_available_date", {
            d: getLastWindowDate().format("MMM DD"),
        });
    }

    function getVisioButtons() {
        const visualizations = ["%", "#"];
        const isVisioAllowed = channelAnalysisMetric === "TrafficShare" && !isPeriodOverPeriod();

        return isVisioAllowed ? (
            <StyledSwitcher
                selectedIndex={selectedVisualizationIndex}
                customClass="CircleSwitcher"
                onItemClick={(index) => onVisualizationClick(index, visualizations[index])}
            >
                {visualizations.map((visItem) => {
                    return <CircleSwitcherItem key={visItem}>{visItem}</CircleSwitcherItem>;
                })}
            </StyledSwitcher>
        ) : null;
    }

    const onVisualizationClick = (index: number, item: string) => {
        setSelectedVisualizationIndex(index);
        allTrackers.dispatchTrackEvent("Measure Button", "click", `${channelAnalysisMetric || ""}/${item || ""}`);
    };

    const isShowMMXAlertBell = () =>
        (webSource === devicesTypes.MOBILE || webSource === devicesTypes.TOTAL) &&
        startDate.isBefore(dayjs(newMMXAlgoStartData)) &&
        endDate.isAfter(dayjs(newMMXAlgoStartData));

    const mtdTooltipText = getMTDTooltipContent();
    const getAddToDashboard = useCallback(
        ({ metric, selectedChannel }) => {
            return (
                <AddToDashboard
                    webSource={webSource}
                    type={mapTabToMetricAndType[metric].type}
                    metric={mapTabToMetricAndType[metric].metric}
                    modelType="fromWebsite"
                    filters={{
                        timeGranularity: duration === "28d" ? "Daily" : "Monthly",
                    }}
                    overrideAddToDashboardParams={{
                        ...(!!selectedChannel && { selectedChannel }),
                    }}
                />
            );
        },
        [webSource, duration],
    );

    function getInsights(): IInsightProps[] {
        if (showInsights) {
            const parsedData = parseData(
                data,
                selectedGranularity.value,
                "TrafficShare",
                isMTDActive,
                getLastWindowDate(),
                chosenSites,
            );
            return calcInsights(parsedData, selectedGranularity.value, false);
        }
    }

    const getTitleTooltip = () => {
        return isComparePop()
            ? i18nFilter()("wa.mmx.channel_analysis.compare_pop.title.tooltip")
            : webSource === devicesTypes.TOTAL
              ? i18nFilter()("wa.mmx.allTraffic.channel_analysis.title.tooltip")
              : i18nFilter()("wa.mmx.channel_analysis.title.tooltip");
    };

    const getTitle = () => {
        return isComparePop()
            ? i18nFilter()("wa.mmx.channel_analysis.compare_pop.title")
            : i18nFilter()("wa.mmx.channel_analysis.new.title");
    };
    return (
        <Wrapper
            ref={wrapperRef}
            className={"ChannelAnalysisChartContainer"}
        >
            <BoxContainer data-automation-channel-analysis={true}>
                <Title>
                    <FlexColumn>
                        <PrimaryBoxTitle
                            tooltip={getTitleTooltip()}
                            customElement={
                                isShowMMXAlertBell() ? (
                                    <MMXAlertWrapper>
                                        <MMXAlertWithPlainTooltip
                                            text={
                                                webSource === devicesTypes.TOTAL
                                                    ? "mmx.total.new.algo.subtitle.filters.tooltip.content"
                                                    : null
                                            }
                                        />
                                    </MMXAlertWrapper>
                                ) : null
                            }
                        >
                            {getTitle()}
                        </PrimaryBoxTitle>
                        <SubTitleChannelAnalysisChart>
                            <SubTitle
                                fromDate={fromDate}
                                toDate={toDate}
                                compareFromDate={compareFromDate}
                                compareToDate={compareToDate}
                                duration={duration}
                                country={country}
                                webSource={webSource}
                                onDateClick={onDateClick}
                                onCountryClick={onCountryClick}
                                onDateHover={onDateHover}
                                onCountryHover={onCountryHover}
                                onWebSourceHover={onWebSourceHover}
                            />
                        </SubTitleChannelAnalysisChart>
                    </FlexColumn>
                    <RightSection>
                        {isMTDSupported ? (
                            <PlainTooltip
                                enabled={!!mtdTooltipText}
                                tooltipContent={mtdTooltipText}
                            >
                                <MTDTitle onClick={toggleMTD}>
                                    <OnOffSwitch
                                        isSelected={isMTDActive}
                                        onClick={_.noop}
                                    />
                                    <StyledBoxSubtitle>
                                        <MTDLabel>{i18nFilter()("wa.mmx.channel_analysis.mtd.toggle_label")}</MTDLabel>
                                    </StyledBoxSubtitle>
                                </MTDTitle>
                            </PlainTooltip>
                        ) : null}
                        {getVisioButtons()}
                        <SwitcherGranularityContainer
                            itemList={availableGranularities}
                            selectedIndex={selectedGranularity.index}
                            className={"gran-switch"}
                            onItemClick={toggleGranularity}
                        />
                        <HeaderActions data-automation="channel-analysis-header-actions">
                            <DownloadButtonMenu
                                Excel={true}
                                downloadUrl={getExcelUrl()}
                                exportFunction={handleExcelClick}
                                excelLocked={!downloadExcelPermitted}
                                PaidExcel={isPaidExcelPermitted}
                                totalCount={excelTotalCount}
                            />
                            <DownloadButtonMenu
                                PNG={true}
                                handleDownloadFunction={handlePngDownload}
                            />
                            {!isPoP
                                ? getAddToDashboard({
                                      metric: channelAnalysisMetric,
                                      selectedChannel: isCompare ? safeSelectedChannel : undefined,
                                  })
                                : null}
                        </HeaderActions>
                    </RightSection>
                </Title>
                <Content>
                    <>
                        {isFetching ? (
                            <Loader />
                        ) : (
                            <ChannelAnalysisChart
                                ref={graphComponentRef}
                                isLoading={isFetching}
                                startDate={startDate.format("YYYY-MM-DD")}
                                endDate={endDate.format("YYYY-MM-DD")}
                                mtdStartDate={endDate.clone().add(1, "days").startOf("day").format("YYYY-MM-DD")}
                                mtdEndDate={getLastWindowDate().format("YYYY-MM-DD")}
                                isMTDOn={isMTDActive}
                                selectedGranularity={selectedGranularity.value}
                                data={data}
                                isPoP={isPoP}
                                keys={keys}
                                webSource={webSource}
                                durationObject={durationObject}
                                selectedChannelFilter={channelAnalysisChannel}
                                handleChannelFilterChange={handleChannelFilterChange}
                                selectedMetric={channelAnalysisMetric}
                                handleMetricChange={handleMetricChange}
                                showInsights={showInsights}
                                domain={key}
                                insights={insights}
                                selectedVisualizationIndex={selectedVisualizationIndex}
                                setSelectedVisualizationIndex={setSelectedVisualizationIndex}
                                isShowMMXAlertBell={isShowMMXAlertBell()}
                                activeChannels={activeChannels}
                                filterChannels={filterChannels}
                                chartColors={
                                    webSource === devicesTypes.TOTAL
                                        ? CHART_COLORS.trafficSourcesColorsBySourceMMX
                                        : undefined
                                }
                                trafficChannelsCount={trafficChannelsCount}
                                getHdBannerCtaState={getHdBannerCtaState}
                            />
                        )}
                    </>
                </Content>
            </BoxContainer>
        </Wrapper>
    );
};

export default ChannelAnalysisChartContainer;
